import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';


const STORAGE_KEY = 'qg_cookie_consent';

export default function CookieBanner() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false, functional: true });

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) setVisible(true);
  }, []);

  const saveConsent = async (preference, analytics, marketing, functional) => {
    const payload = { preference, analytics, marketing, functional };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    setVisible(false);
    setShowCustomize(false);
    if (user) {
      try {
        await fetch('/api/consent/cookie', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: user.id, ...payload }),
        });
      } catch {}
    }
  };

  const handleAcceptAll = () => saveConsent('all', true, true, true);
  const handleRejectNonEssential = () => saveConsent('essential', false, false, true);
  const handleSaveCustom = () => saveConsent('custom', prefs.analytics, prefs.marketing, true);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Cookie consent"
      style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999 }}
      className="bg-gray-950 border-t border-gray-800 shadow-2xl"
    >
      {!showCustomize ? (
        <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-300 leading-relaxed">
              We use cookies to improve your experience and analyze site usage. By clicking{' '}
              <strong className="text-white">Accept All</strong> you consent to our use of all cookies. You can{' '}
              <button
                onClick={() => setShowCustomize(true)}
                className="text-yellow-500 hover:text-yellow-400 underline font-medium focus:outline-none"
              >
                customize
              </button>{' '}
              your preferences or{' '}
              <strong className="text-white">Reject Non-Essential</strong> cookies. See our{' '}
              <Link to="/privacy" className="text-yellow-500 hover:text-yellow-400 underline font-medium">
                Privacy Policy
              </Link>
              .
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
            <button
              onClick={() => setShowCustomize(true)}
              className="px-4 py-2 text-sm text-gray-400 border border-gray-700 hover:border-gray-500 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
            >
              Customize
            </button>
            <button
              onClick={handleRejectNonEssential}
              className="px-4 py-2 text-sm text-gray-300 border border-gray-600 hover:border-gray-400 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
            >
              Reject Non-Essential
            </button>
            <button
              onClick={handleAcceptAll}
              className="px-5 py-2 text-sm font-semibold text-black rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
              style={{ background: '#C9A84C' }}
            >
              Accept All
            </button>
          </div>
        </div>
      ) : (
        <div className="max-w-2xl mx-auto px-4 py-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold text-base">Cookie Preferences</h3>
            <button
              onClick={() => setShowCustomize(false)}
              className="text-gray-400 hover:text-gray-200 focus:outline-none"
              aria-label="Back"
            >
              &larr; Back
            </button>
          </div>
          <div className="space-y-3 mb-5">
            <CookieToggle
              label="Essential Cookies"
              description="Required for the site to function. Cannot be disabled."
              checked={true}
              disabled
            />
            <CookieToggle
              label="Analytics Cookies"
              description="Help us understand how you use our site to improve it."
              checked={prefs.analytics}
              onChange={v => setPrefs(p => ({ ...p, analytics: v }))}
            />
            <CookieToggle
              label="Marketing Cookies"
              description="Used to deliver relevant ads and track campaign effectiveness."
              checked={prefs.marketing}
              onChange={v => setPrefs(p => ({ ...p, marketing: v }))}
            />
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleRejectNonEssential}
              className="flex-1 py-2 text-sm text-gray-300 border border-gray-600 hover:border-gray-400 rounded-lg transition-colors"
            >
              Reject All Non-Essential
            </button>
            <button
              onClick={handleSaveCustom}
              className="flex-1 py-2 text-sm font-semibold text-black rounded-lg transition-colors"
              style={{ background: '#C9A84C' }}
            >
              Save Preferences
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function CookieToggle({ label, description, checked, onChange, disabled }) {
  return (
    <div className="flex items-start gap-3 p-3 bg-gray-900 rounded-lg border border-gray-800">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white">{label}</p>
        <p className="text-xs text-gray-400 mt-0.5">{description}</p>
      </div>
      <button
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => !disabled && onChange && onChange(!checked)}
        className={`relative flex-shrink-0 mt-0.5 w-10 h-6 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-500/50 ${
          checked ? 'bg-yellow-500' : 'bg-gray-700'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span
          className={`block w-4 h-4 bg-white rounded-full shadow transition-transform mt-1 ${
            checked ? 'translate-x-5' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}
